import { useState, useEffect, useCallback, useMemo } from 'react';
import { ScenarioDefinitionType } from '../../api/scenarioDefinitions';
import { ScenarioCharacterCard } from './ScenarioCharacterCard';
import styles from './EditScenarioCharacters.module.css';
import { useDevice } from '../../context/DeviceContext';
import { CharacterListResponse } from '../../api/types';
import EditScenarioCharactersSearch from './EditScenarioCharactersSearch';
import { ScenarioCharacter } from '../../api/types/scenario';
import { api } from '../../api';

interface EditScenarioCharactersProps {
  definition: ScenarioDefinitionType;
  setDefinition: React.Dispatch<React.SetStateAction<ScenarioDefinitionType>>;
}

function EditScenarioCharacters({ definition, setDefinition }: EditScenarioCharactersProps): JSX.Element {
  const { isMobile } = useDevice();
  const [showSearch, setShowSearch] = useState(!isMobile); // Always show on desktop
  const [newCharacterId, setNewCharacterId] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize existing character IDs to prevent unnecessary recalculations and re-renders
  const existingCharacterIds = useMemo(() => {
    const scenarioCharacterIds = definition.characters?.map(char => char.id) || [];
    const worldCharacterIds = definition.available_characters?.map(char => char.id) || [];
    return [...scenarioCharacterIds, ...worldCharacterIds];
  }, [definition.characters, definition.available_characters]);

  // Handle device type changes
  useEffect(() => {
    if (!isMobile) {
      setShowSearch(true);
    }
  }, [isMobile]);

  // Clear the new character animation after a delay
  useEffect(() => {
    if (newCharacterId) {
      const timer = setTimeout(() => {
        setNewCharacterId(null);
      }, 1000); // Clear after animation completes
      
      return () => clearTimeout(timer);
    }
  }, [newCharacterId]);

  // Helper to update definition with error handling
  const updateDefinitionSafely = useCallback(async (updateFn: () => Promise<ScenarioDefinitionType>) => {
    setIsUpdating(true);
    setError(null);
    
    try {
      const updatedDefinition = await updateFn();
      setDefinition(updatedDefinition);
      return true;
    } catch (err) {
      console.error('Update operation failed:', err);
      setError(err instanceof Error ? err.message : 'Operation failed');
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [setDefinition]);

  const handleAddCharacter = () => {
    setShowSearch(true);
  };

  const handleCloseSearch = () => {
    if (isMobile) {
      setShowSearch(false);
    }
  };

  const handleSelectCharacter = useCallback(async (character: CharacterListResponse) => {
    // Prevent closing search modal during operation
    setIsUpdating(true);
    setError(null);
    
    try {
      // Use updateDefinitionSafely but don't let it set isUpdating because we want
      // to control when we exit the updating state ourselves
      const updatedDefinition = await definition.addCharacter(character.id);
      setDefinition(updatedDefinition);
      setNewCharacterId(character.id);
      
      // Important: Don't hide the search modal after adding a character
      // This prevents the flickering effect
    } catch (err) {
      console.error('Failed to add character:', err);
      setError(err instanceof Error ? err.message : 'Failed to add character');
    } finally {
      setIsUpdating(false);
    }
  }, [definition, setDefinition]);

  const handleDeleteCharacter = useCallback(async (characterId: string) => {
    const success = await updateDefinitionSafely(async () => {
      return await definition.removeCharacter(characterId);
    });
    
    if (!success) {
      setError('Failed to remove character. Please try again.');
    }
  }, [definition, updateDefinitionSafely]);

  // Improved character update handler
  const handleCharacterUpdated = useCallback(async (updatedCharacter: ScenarioCharacter) => {
    // Only proceed if character is in current definition
    const characterIndex = definition.characters.findIndex(char => char.id === updatedCharacter.id);
    if (characterIndex === -1) return;
    
    setIsUpdating(true);
    setError(null);
    
    try {
      // If we have a username, fetch fresh data to ensure we have latest image URL
      if (updatedCharacter.username) {
        const freshCharacter = await api.characters.get(updatedCharacter.username);
        
        // Map Character type to ScenarioCharacter type
        updatedCharacter = {
          id: freshCharacter.id,
          username: freshCharacter.username,
          display_name: freshCharacter.display_name,
          tagline: freshCharacter.tagline,
          icon: freshCharacter.icon,
          is_mature: freshCharacter.is_mature
        };
      }
      
      // Create a new definition using proper immutable update pattern
      setDefinition(prevDef => {
        // First create a proper copy to maintain prototype methods
        const updatedDef = Object.create(
          Object.getPrototypeOf(prevDef),
          Object.getOwnPropertyDescriptors(prevDef)
        );
        
        // Then update only the characters array using immutable pattern
        updatedDef.characters = prevDef.characters.map(char => 
          char.id === updatedCharacter.id ? updatedCharacter : char
        );
        
        return updatedDef;
      });
    } catch (err) {
      console.error('Failed to update character:', err);
      setError('Failed to update character. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  }, [definition, setDefinition]);

  // Helper function to determine if a character should have the animation class
  const shouldAnimateCharacter = useCallback((characterId: string): boolean => {
    return characterId === newCharacterId;
  }, [newCharacterId]);

  return (
    <div className={styles.editCharactersContainer}>
      <div className={`${styles.sectionHeader} ${isMobile ? styles.mobileHeader : ''}`}>
        <h3 className={styles.sectionTitle}>Characters</h3>
        <div className={styles.headerButtons}>
          {isMobile && (
            <button 
              onClick={handleAddCharacter}
              className={styles.addButton}
              aria-label="Add new character"
              disabled={isUpdating}
            >
              +
            </button>
          )}
        </div>
      </div>
      
      {error && <div className={styles.errorMessage}>{error}</div>}
      
      <div className={styles.charactersGrid}>
        {isUpdating && <div className={styles.loadingOverlay}>Updating...</div>}
        
        {definition.characters && definition.characters.length > 0 ? (
          definition.characters.map((character) => (
            <div 
              key={character.id}
              className={shouldAnimateCharacter(character.id) ? styles.newCharacterAnimation : ''}
            >
              <ScenarioCharacterCard 
                character={character}
                showDeleteButton={true}
                onDeleteClick={() => handleDeleteCharacter(character.id)}
                isSearchResult={false}
                onCharacterUpdated={handleCharacterUpdated}
              />
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            <p>No characters have been added to this scenario yet.</p>
            {isMobile && (
              <button 
                className={styles.emptyStateButton}
                onClick={handleAddCharacter}
                disabled={isUpdating}
              >
                Add Characters
              </button>
            )}
          </div>
        )}
      </div>
      
      <EditScenarioCharactersSearch
        isVisible={showSearch}
        onClose={handleCloseSearch}
        onSelectCharacter={handleSelectCharacter}
        existingCharacterIds={existingCharacterIds}
        isUpdating={isUpdating}
      />
    </div>
  );
}

export default EditScenarioCharacters; 